<template>
  <div class="fu-drt" data-test="fu-drawer-tabs">
    <div class="fu-drt__barwrap">
    <div ref="bar" class="fu-drt__bar" role="tablist" aria-label="Patient details" @scroll="updateEdges">
      <button
        v-for="(t, i) in tabs"
        :id="`${uid}-tab-${t.key}`"
        :key="t.key"
        ref="tab"
        type="button"
        role="tab"
        class="fu-drt__tab"
        :class="{ 'fu-drt__tab--on': t.key === current }"
        :aria-selected="String(t.key === current)"
        :aria-controls="`${uid}-panel`"
        :tabindex="t.key === current ? 0 : -1"
        :data-test="`fu-drawer-tab-${t.key}`"
        @click="$emit('select', t.key)"
        @keydown.right.prevent="move(i, 1)"
        @keydown.left.prevent="move(i, -1)"
      >{{ t.label }}<span v-if="t.count > 0" class="fu-drt__count">{{ t.count }}</span></button>
    </div>
    <!-- More tabs than fit: a fade + arrow on the side that hides some (the bar scrolls, its scrollbar
         is hidden). Mouse-only — keyboard users move with ← / →, which scrolls the tab into view. -->
    <button
      v-if="canLeft"
      type="button"
      class="fu-drt__more fu-drt__more--left"
      tabindex="-1"
      aria-label="Show earlier tabs"
      data-test="fu-drawer-tabs-left"
      @click="scrollTabs(-1)"
    ><svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false"><path d="M10 3.5 5.5 8l4.5 4.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" /></svg></button>
    <button
      v-if="canRight"
      type="button"
      class="fu-drt__more fu-drt__more--right"
      tabindex="-1"
      aria-label="Show more tabs"
      data-test="fu-drawer-tabs-right"
      @click="scrollTabs(1)"
    ><svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false"><path d="M6 3.5 10.5 8 6 12.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" /></svg></button>
    </div>

    <div
      v-if="currentTab"
      :id="`${uid}-panel`"
      class="fu-drt__panel"
      role="tabpanel"
      :aria-labelledby="`${uid}-tab-${currentTab.key}`"
      :data-test="`fu-drawer-panel-${currentTab.key}`"
    >
      <component
        :is="panelOf(currentTab.key)"
        :tab="currentTab"
        :ctx="c"
        @edit-note="$emit('edit-note', $event)"
        @open-patient="$emit('open-patient', $event)"
      />
    </div>
    <p v-else class="fu-drt__none">Nothing to show for this patient yet.</p>
  </div>
</template>

<script>
/**
 * DrawerTabs — the right-hand card: the design's tab bar and the open tab's panel (one component per
 * tab). `tabs` = drawerTabs.buildDrawerTabs() — the design's tabs, today's data, a tab with no data
 * source left out — passed through drawerTabs.withTasksTab() for today's "Tasks" tab (LAST, only when
 * the patient has tagged tasks).
 *
 *   tabs    [{ key, label, count, sections: [{ key, legacyTitle, rows, count, shown }] }]
 *   active  the open tab's key (the first tab when it is not in `tabs`)
 *   ctx     what the panels need and the container owns (all optional):
 *           formatDate(v), formatDateTime(v), formatMoney(n), formatTooth(n) (Canada's FDI numbering
 *           lives in the mixin), employeeName(id), ageOf(birthdate), canEditNotes ($can('edit',
 *           'followupNote')), savingNoteId (TaskID being saved)
 * Events: select(key), edit-note({ activity, oldNote, newNote }), open-patient(PatNum).
 * Presentational: no store, no mixin. Arrow keys move between tabs.
 */
import DrawerActivityTab from './DrawerActivityTab.vue';
import DrawerCommTab from './DrawerCommTab.vue';
import DrawerAppointmentsTab from './DrawerAppointmentsTab.vue';
import DrawerTreatmentTab from './DrawerTreatmentTab.vue';
import DrawerFamilyTab from './DrawerFamilyTab.vue';
import DrawerInsuranceTab from './DrawerInsuranceTab.vue';
import DrawerDefinitionsTab from './DrawerDefinitionsTab.vue';
import DrawerStatusHistoryTab from './DrawerStatusHistoryTab.vue';
import DrawerFamilyClaimsTab from './DrawerFamilyClaimsTab.vue';
import DrawerBrokenTab from './DrawerBrokenTab.vue';
import DrawerTasksTab from './DrawerTasksTab.vue';

const PANELS = {
  activity: 'DrawerActivityTab',
  comm: 'DrawerCommTab',
  appts: 'DrawerAppointmentsTab',
  tx: 'DrawerTreatmentTab',
  family: 'DrawerFamilyTab',
  ins: 'DrawerInsuranceTab',
  claim: 'DrawerDefinitionsTab',
  preauth: 'DrawerDefinitionsTab',
  history: 'DrawerStatusHistoryTab',
  famclaims: 'DrawerFamilyClaimsTab',
  broken: 'DrawerBrokenTab',
  tasks: 'DrawerTasksTab',
};

const DEFAULT_CTX = {
  formatDate: v => (v ? String(v).slice(0, 10) : ''),
  formatDateTime: v => (v ? String(v) : ''),
  formatMoney: n => (n === null || n === undefined || n === '' ? '' : `$${Number(n).toFixed(2)}`),
  formatTooth: n => (n === null || n === undefined ? '' : String(n)),
  employeeName: id => (id === null || id === undefined ? '' : String(id)),
  ageOf: () => '',
  canEditNotes: false,
  savingNoteId: null,
};

let seq = 0;

export default {
  name: 'DrawerTabs',
  components: {
    DrawerActivityTab, DrawerCommTab, DrawerAppointmentsTab, DrawerTreatmentTab, DrawerFamilyTab, DrawerInsuranceTab,
    DrawerDefinitionsTab, DrawerStatusHistoryTab, DrawerFamilyClaimsTab, DrawerBrokenTab, DrawerTasksTab,
  },
  props: {
    tabs: { type: Array, default: () => [] },
    active: { type: String, default: null },
    ctx: { type: Object, default: () => ({}) },
  },
  data() {
    seq += 1;
    return { uid: `fu-drtabs-${seq}`, canLeft: false, canRight: false };
  },
  watch: {
    tabs() { this.$nextTick(() => { this.updateEdges(); this.revealCurrent(); }); },
    current() { this.$nextTick(() => this.revealCurrent()); },
  },
  mounted() {
    this.$nextTick(() => { this.updateEdges(); this.revealCurrent(); });
    // The drawer's width changes (phone / tablet / desktop layouts, window resize): re-check the edges.
    if (typeof window !== 'undefined' && typeof window.ResizeObserver === 'function' && this.$refs.bar) {
      this.ro = new window.ResizeObserver(() => this.updateEdges());
      this.ro.observe(this.$refs.bar);
    }
  },
  beforeDestroy() {
    if (this.ro) this.ro.disconnect();
    this.ro = null;
  },
  computed: {
    current() {
      if (this.tabs.some(t => t.key === this.active)) return this.active;
      return this.tabs[0] ? this.tabs[0].key : null;
    },
    currentTab() {
      return this.tabs.find(t => t.key === this.current) || null;
    },
    c() {
      return Object.assign({}, DEFAULT_CTX, this.ctx || {});
    },
  },
  methods: {
    panelOf(key) {
      return PANELS[key] || 'DrawerActivityTab';
    },
    // Which sides of the scrolling bar hide tabs (1px slack for sub-pixel widths).
    updateEdges() {
      const bar = this.$refs.bar;
      if (!bar) return;
      const max = bar.scrollWidth - bar.clientWidth;
      this.canLeft = max > 1 && bar.scrollLeft > 1;
      this.canRight = max > 1 && bar.scrollLeft < max - 1;
    },
    // Arrow click: scroll most of a bar-width, so the hidden tabs come in and one stays for context.
    scrollTabs(dir) {
      const bar = this.$refs.bar;
      if (!bar) return;
      const by = dir * Math.max(120, Math.round(bar.clientWidth * 0.6));
      if (typeof bar.scrollBy === 'function') bar.scrollBy({ left: by, behavior: 'smooth' });
      else bar.scrollLeft += by;
      this.updateEdges();
    },
    // The open tab is never left hidden past an edge (e.g. a queue that opens on its last tab).
    revealCurrent() {
      const bar = this.$refs.bar;
      const i = this.tabs.findIndex(t => t.key === this.current);
      const el = i >= 0 && this.$refs.tab ? this.$refs.tab[i] : null;
      if (!bar || !el) return;
      const left = el.offsetLeft - bar.offsetLeft;
      const right = left + el.offsetWidth;
      if (left < bar.scrollLeft) bar.scrollLeft = Math.max(0, left - 24);
      else if (right > bar.scrollLeft + bar.clientWidth) bar.scrollLeft = right - bar.clientWidth + 24;
      this.updateEdges();
    },
    move(i, step) {
      if (!this.tabs.length) return;
      const next = (i + step + this.tabs.length) % this.tabs.length;
      this.$emit('select', this.tabs[next].key);
      this.$nextTick(() => {
        const el = this.$refs.tab && this.$refs.tab[next];
        if (el && el.focus) el.focus();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import './drawer';

.fu-drt__barwrap {
  position: relative;
}

// Fade + arrow over the bar's edge while tabs hide past it. Sits above the bar's bottom border.
.fu-drt__more {
  @include fu-button-reset;
  position: absolute;
  top: 0;
  bottom: 1px;
  width: 44px;
  display: flex;
  align-items: center;
  color: $fu-neutral-600;
  cursor: pointer;
  z-index: 1;
  &:hover { color: $fu-text; }
}

.fu-drt__more--right {
  right: 0;
  justify-content: flex-end;
  padding-right: 10px;
  background: linear-gradient(to right, rgba($fu-surface, 0), $fu-surface 55%);
}

.fu-drt__more--left {
  left: 0;
  justify-content: flex-start;
  padding-left: 10px;
  background: linear-gradient(to left, rgba($fu-surface, 0), $fu-surface 55%);
}

.fu-drt__bar {
  display: flex;
  gap: 20px;
  padding: 0 18px;
  border-bottom: 1px solid $fu-border;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
}

.fu-drt__tab {
  @include fu-button-reset;
  @include fu-focus-ring;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 15px 1px 13px;
  white-space: nowrap;
  font-size: 13.5px;
  font-weight: $fu-fw-bold;
  color: $fu-text-subtle;
  &:hover { color: $fu-neutral-700; }
}

.fu-drt__tab--on {
  color: $fu-text;
  box-shadow: inset 0 -2px 0 $fu-brand;
  &:hover { color: $fu-text; }
}

.fu-drt__count {
  @include fu-tabular;
  min-width: 20px;
  height: 18px;
  padding: 0 6px;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: $fu-fw-bold;
  background: $fu-neutral-100;
  color: $fu-neutral-600;
}

.fu-drt__tab--on .fu-drt__count { background: $fu-brand; color: #ffffff; }

.fu-drt__panel { padding: 2px 18px 16px; min-width: 0; }

.fu-drt__none { @include fu-dr-empty; }

.fu-dr--phone .fu-drt__bar { padding: 0 14px; gap: 16px; }
.fu-dr--phone .fu-drt__panel { padding: 2px 14px 14px; }
</style>
